import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Heart,
  Layers3,
  Repeat2,
  Search,
  Star,
  Trophy,
} from 'lucide-react';
import apiClient from '../services/apiClient';
import PracticeSettingsService from '../services/PracticeSettingsService';
import { getCardLevelUpAnimation } from '../services/LevelUpCelebrationAssets';
import { unlockPracticeAudio } from '../services/soundEffects';
import { selectTriumphReviewCards } from '../services/TriumphReviewSession';
import TriumphReviewTraining from './TriumphReviewTraining';
import MarkdownText from './MarkdownText';
import Spinner from './Spinner';
import ErrorMessage from './ErrorMessage';
import ReviewLevelUpDialog from './ReviewLevelUpDialog';
import CelebrationPopup from './CelebrationPopup';
import { getGraduationStamp } from '../utils/graduationStamp';
import { getLanguage, t } from '../i18n';
import './TriumphPage.css';

const LEGACY_COMPLETED_AT = '2026-07-03T00:00:00.000Z';
const pageSize = 10;

function formatNumber(value = 0) {
  return new Intl.NumberFormat(getLanguage() === 'ko' ? 'ko-KR' : 'en-US').format(Number(value) || 0);
}

function formatStatNumber(value = 0) {
  return formatNumber(Math.round(Number(value) || 0));
}

function formatDate(value) {
  const date = new Date(value || LEGACY_COMPLETED_AT);
  if (Number.isNaN(date.getTime())) return formatDate(LEGACY_COMPLETED_AT);
  return date.toLocaleDateString(getLanguage() === 'ko' ? 'ko-KR' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatAverageDays(value = 0) {
  return t('learningNavigation.triumph.days', undefined, { count: formatStatNumber(Math.max(0, Number(value) || 0)) });
}

function StatRowHeader({ label, icon: Icon }) {
  return (
    <span className="triumph-stats-row-icon" title={label} aria-label={label}>
      <Icon size={17} strokeWidth={2.4} aria-hidden="true" />
    </span>
  );
}

function isCompletedCard(card) {
  if (card?.isReadingGroup && Array.isArray(card.readingChildren)) {
    return card.readingChildren.some(PracticeSettingsService.isGraduated);
  }
  return PracticeSettingsService.isGraduated(card);
}

function matchesTriumphHighlight(card, highlightedCardId) {
  if (!card || highlightedCardId == null) return false;
  const targetId = String(highlightedCardId);
  return String(card.id) === targetId || (card.readingChildren || []).some((child) => (
    String(child.id) === targetId
  ));
}

export function isTriumphLevelUpEligible(card) {
  return Boolean(
    card &&
    !card.isReadingGroup &&
    PracticeSettingsService.isGraduated(card)
  );
}

function getCompletedCardUnits(cards = []) {
  return cards.flatMap((card) => (
    card?.isReadingGroup && Array.isArray(card.readingChildren)
      ? card.readingChildren.filter(PracticeSettingsService.isGraduated)
      : (PracticeSettingsService.isGraduated(card) ? [card] : [])
  ));
}

function getCompletedAt(card) {
  if (card?.isReadingGroup && Array.isArray(card.readingChildren)) {
    const completedDates = card.readingChildren
      .filter(PracticeSettingsService.isGraduated)
      .map((child) => child.completedAt)
      .filter(Boolean)
      .sort();
    if (completedDates.length) return completedDates[completedDates.length - 1];
  }
  return card?.completedAt || LEGACY_COMPLETED_AT;
}

function getNoteId(note) {
  return note?.id || note?.noteId || note?._id;
}

function buildNoteById(notes = []) {
  return new Map((notes || [])
    .map((note) => [getNoteId(note), note])
    .filter(([noteId]) => Boolean(noteId)));
}

function getNoteCreatedAtForCard(card, noteById) {
  const note = noteById?.get(card?.noteId);
  return note?.createdAt || note?.created_at || card?.noteCreatedAt || card?.createdAt || null;
}

function buildReadingChildSuggestion(group, child) {
  const siblings = Array.isArray(group?.readingChildren) ? group.readingChildren : [];
  const ordered = [...siblings].sort((a, b) => Number(a.readingOrder || 0) - Number(b.readingOrder || 0));
  const index = ordered.findIndex((item) => String(item.id) === String(child?.id));
  const previous = index > 0 ? ordered[index - 1] : null;
  const next = index >= 0 && index < ordered.length - 1 ? ordered[index + 1] : null;
  const isSpeech = child?.readingItemType === 'speech';
  return {
    ...child,
    readingTitle: group?.readingTitle || group?.front || '',
    suggestedQuestion: isSpeech ? String(child?.front || '') : '',
    suggestedAnswer: isSpeech
      ? String(child?.back || '')
      : String(child?.readingItemText || child?.back || ''),
    suggestedContextPre: isSpeech ? String(previous?.back || previous?.readingItemText || '') : '',
    suggestedContextAfter: isSpeech ? String(next?.back || next?.readingItemText || '') : '',
  };
}

export function calculateCompletionDays(card, noteById = new Map()) {
  const createdAt = getNoteCreatedAtForCard(card, noteById);
  const completedAt = getCompletedAt(card);
  const createdMs = createdAt ? new Date(createdAt).getTime() : NaN;
  const completedMs = completedAt ? new Date(completedAt).getTime() : NaN;

  if (!Number.isFinite(createdMs) || !Number.isFinite(completedMs)) return null;
  return Math.max(0, (completedMs - createdMs) / (24 * 60 * 60 * 1000));
}

export function buildTypeStats(cards, cardType, noteById = new Map()) {
  const typedCards = getCompletedCardUnits(cards)
    .filter((card) => PracticeSettingsService.getCardType(card) === cardType);
  const count = typedCards.length;
  const repeatTotal = typedCards.reduce((sum, card) => sum + PracticeSettingsService.getCardPracticeCount(card), 0);
  const scoreTotal = typedCards.reduce((sum, card) => sum + PracticeSettingsService.getCardScore(card), 0);
  const completionDays = typedCards
    .map((card) => calculateCompletionDays(card, noteById))
    .filter((value) => Number.isFinite(value));
  const completionDayTotal = completionDays.reduce((sum, value) => sum + value, 0);

  return {
    count,
    averageRepeat: count ? repeatTotal / count : 0,
    averageScore: count ? scoreTotal / count : 0,
    averageCompletionDays: completionDays.length ? completionDayTotal / completionDays.length : 0,
    completionDaySampleCount: completionDays.length,
  };
}

function TriumphPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const highlightedCardId = location.state?.highlightCardId != null
    ? String(location.state.highlightCardId)
    : null;
  const [cards, setCards] = useState([]);
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [savingFavoriteId, setSavingFavoriteId] = useState(null);
  const [levelUpCard, setLevelUpCard] = useState(null);
  const [readingPicker, setReadingPicker] = useState(null);
  const [levelUpCelebration, setLevelUpCelebration] = useState(null);
  const [reviewTraining, setReviewTraining] = useState(null);
  const [startingTraining, setStartingTraining] = useState(false);
  const [trainingError, setTrainingError] = useState(null);
  const startingTrainingRef = useRef(false);
  const trainingStartButtonRef = useRef(null);
  const wasTrainingRef = useRef(false);

  useEffect(() => {
    const loadCards = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [loadedCards, loadedNotes] = await Promise.all([
          apiClient.getAllFlashCards({ status: 'graduated', view: 'triumph' }),
          apiClient.getAllNotes().catch(() => []),
        ]);
        setCards((loadedCards || []).filter(isCompletedCard));
        setNotes(loadedNotes || []);
      } catch (err) {
        console.error('Failed to load triumph cards:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadCards();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [query, favoritesOnly]);

  const filteredCards = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    const sortedCards = cards
      .filter((card) => {
        if (favoritesOnly && !card.isFavorite) return false;
        if (!normalizedQuery) return true;
        const childText = (card.readingChildren || [])
          .map((child) => `${child.front || ''} ${child.back || ''}`)
          .join(' ');
        return `${card.front || ''} ${card.back || ''} ${childText}`
          .toLowerCase()
          .includes(normalizedQuery);
      })
      .sort((a, b) => new Date(getCompletedAt(b)).getTime() - new Date(getCompletedAt(a)).getTime());

    const highlightedIndex = sortedCards.findIndex((card) => (
      matchesTriumphHighlight(card, highlightedCardId)
    ));
    if (highlightedIndex <= 0) return sortedCards;
    return [
      sortedCards[highlightedIndex],
      ...sortedCards.slice(0, highlightedIndex),
      ...sortedCards.slice(highlightedIndex + 1),
    ];
  }, [cards, favoritesOnly, highlightedCardId, query]);

  const totalPages = Math.max(1, Math.ceil(filteredCards.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const visibleCards = filteredCards.slice((safePage - 1) * pageSize, safePage * pageSize);
  const noteById = useMemo(() => buildNoteById(notes), [notes]);
  const simpleStats = buildTypeStats(cards, PracticeSettingsService.CARD_TYPES.SIMPLE, noteById);
  const normalStats = buildTypeStats(cards, PracticeSettingsService.CARD_TYPES.NORMAL, noteById);
  const readingStats = buildTypeStats(cards, PracticeSettingsService.CARD_TYPES.READING, noteById);
  const completedCardCount = getCompletedCardUnits(cards).length;
  const trainingCards = selectTriumphReviewCards(cards);

  useEffect(() => {
    if (reviewTraining) wasTrainingRef.current = true;
    else if (wasTrainingRef.current) {
      wasTrainingRef.current = false;
      trainingStartButtonRef.current?.focus();
    }
  }, [reviewTraining]);

  const handleStartTraining = async () => {
    if (startingTrainingRef.current) return;
    startingTrainingRef.current = true;
    setStartingTraining(true);
    setTrainingError(null);
    void unlockPracticeAudio();
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const fullCards = await apiClient.getAllFlashCards({ status: 'graduated' });
      const selected = selectTriumphReviewCards(fullCards || []);
      if (!selected.length) {
        setTrainingError(t('practiceUi.reviewTraining.empty'));
        return;
      }
      const sceneResult = await apiClient.getRandomScene({
        trigger: 'Encounter.before', timezone,
        seed: `triumph-review:${Date.now()}:${Math.random().toString(36).slice(2)}`,
      });
      if (!sceneResult?.scene) {
        setTrainingError(t('practiceUi.reviewTraining.sceneUnavailable'));
        return;
      }
      const access = await apiClient.startPractice({ timezone, mode: 'completedReview' });
      setReviewTraining({ cards: selected, scene: sceneResult.scene, access });
    } catch (err) {
      setTrainingError((err?.code || err?.data?.code) === 'DAILY_PRACTICE_LIMIT_REACHED'
        ? t('practiceUi.reviewTraining.dailyLimit')
        : t('practiceUi.reviewTraining.startFailed'));
    } finally {
      startingTrainingRef.current = false;
      setStartingTraining(false);
    }
  };

  const closeTraining = (updatedCards = [], noteId) => {
    const updatedById = new Map(updatedCards.map(card => [String(card.id || card._id), card]));
    setCards(current => current.map(card => ({ ...card, ...updatedById.get(String(card.id)) })));
    setReviewTraining(null);
    if (noteId) navigate('/map', { state: { noteId } });
  };

  const handleFavoriteToggle = async (card) => {
    const nextFavorite = !card.isFavorite;
    setSavingFavoriteId(card.id);

    try {
      const updatedCard = await apiClient.updateFlashCard(card.id, {
        isFavorite: nextFavorite,
      });
      setCards((current) => current.map((item) => (
        item.id === card.id ? { ...item, ...updatedCard } : item
      )));
    } catch (err) {
      setError(err);
    } finally {
      setSavingFavoriteId(null);
    }
  };

  const openLevelUp = (card) => {
    if (card?.isReadingGroup) {
      const children = (card.readingChildren || [])
        .filter(isTriumphLevelUpEligible)
        .map((child) => buildReadingChildSuggestion(card, child));
      if (children.length === 1) {
        setLevelUpCard(children[0]);
      } else if (children.length > 1) {
        setReadingPicker({ group: card, children });
      }
      return;
    }
    if (isTriumphLevelUpEligible(card)) setLevelUpCard(card);
  };

  if (reviewTraining) return <TriumphReviewTraining {...reviewTraining} onClose={closeTraining} />;

  if (isLoading) {
    return (
      <div className="triumph-page">
        <Spinner size="large" message={t('learningNavigation.triumph.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="triumph-page">
        <ErrorMessage
          error={error}
          onDismiss={() => setError(null)}
          onRetry={() => window.location.reload()}
          type={error?.isNetworkError ? 'network' : 'api'}
        />
      </div>
    );
  }

  return (
    <div className="triumph-page">
      <header className="triumph-page-header">
        <button className="btn-back-inline" onClick={() => navigate('/')} aria-label={t('learningNavigation.common.backHome')} title={t('learningNavigation.common.backHome')}>
          <ArrowLeft size={24} strokeWidth={2.4} aria-hidden="true" />
        </button>
        <div className="triumph-title-block">
          <h1 className="page-title-fx page-title-triumph">{t('learningNavigation.triumph.title')}</h1>
          <p>{t('learningNavigation.triumph.subtitle')}</p>
        </div>
      </header>

      {cards.length === 0 ? (
        <section className="triumph-locked">
          <Trophy size={56} strokeWidth={1.7} aria-hidden="true" />
          <h2>{t('learningNavigation.triumph.lockedTitle')}</h2>
          <p>{t('learningNavigation.triumph.lockedHint')}</p>
        </section>
      ) : (
        <>
          <section className="triumph-training-start" aria-labelledby="triumph-training-prompt">
            <h2 id="triumph-training-prompt">{t('practiceUi.reviewTraining.prompt')}</h2>
            <div className="triumph-training-actions">
              <button type="button" ref={trainingStartButtonRef} onClick={handleStartTraining}
                disabled={startingTraining || trainingCards.length === 0} aria-busy={startingTraining}>
                <Repeat2 size={21} aria-hidden="true" />
                {t(`practiceUi.reviewTraining.${startingTraining ? 'preparing' : 'title'}`)}
              </button>
              {trainingCards.length === 0 && <p>{t('practiceUi.reviewTraining.empty')}</p>}
              {trainingError && <p className="triumph-training-error" role="alert">{trainingError}</p>}
            </div>
          </section>
          <section className="triumph-stats-table-wrap" aria-label={t('learningNavigation.triumph.statistics')}>
            <table className="triumph-stats-table">
              <thead>
                <tr>
                  <th scope="col">{t('learningNavigation.triumph.total', undefined, { count: formatStatNumber(completedCardCount) })}</th>
                  <th scope="col">{t('learningNavigation.common.short')}</th>
                  <th scope="col">{t('learningNavigation.common.sentence')}</th>
                  <th scope="col">{t('learningNavigation.common.reading')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row"><StatRowHeader label={t('learningNavigation.triumph.count')} icon={Layers3} /></th>
                  <td>{formatStatNumber(simpleStats.count)}</td>
                  <td>{formatStatNumber(normalStats.count)}</td>
                  <td>{formatStatNumber(readingStats.count)}</td>
                </tr>
                <tr>
                  <th scope="row"><StatRowHeader label={t('learningNavigation.triumph.averageDays')} icon={CalendarDays} /></th>
                  <td>{formatAverageDays(simpleStats.averageCompletionDays)}</td>
                  <td>{formatAverageDays(normalStats.averageCompletionDays)}</td>
                  <td>{formatAverageDays(readingStats.averageCompletionDays)}</td>
                </tr>
                <tr>
                  <th scope="row"><StatRowHeader label={t('learningNavigation.triumph.averageRepeats')} icon={Repeat2} /></th>
                  <td>{formatStatNumber(simpleStats.averageRepeat)}</td>
                  <td>{formatStatNumber(normalStats.averageRepeat)}</td>
                  <td>{formatStatNumber(readingStats.averageRepeat)}</td>
                </tr>
                <tr>
                  <th scope="row"><StatRowHeader label={t('learningNavigation.triumph.averageScore')} icon={Star} /></th>
                  <td>{formatStatNumber(simpleStats.averageScore)} pt</td>
                  <td>{formatStatNumber(normalStats.averageScore)} pt</td>
                  <td>{formatStatNumber(readingStats.averageScore)} pt</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="triumph-board">
            <div className="triumph-toolbar">
              <label className="triumph-search">
                <Search size={17} aria-hidden="true" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('learningNavigation.triumph.search')}
                  aria-label={t('learningNavigation.triumph.search')}
                />
              </label>
              <button
                type="button"
                className={`triumph-filter-button ${favoritesOnly ? 'active' : ''}`.trim()}
                onClick={() => setFavoritesOnly((value) => !value)}
                aria-pressed={favoritesOnly}
                aria-label={t('learningNavigation.triumph.favorites')}
                title={t('learningNavigation.triumph.favorites')}
              >
                <Star size={17} aria-hidden="true" />
              </button>
            </div>

            {visibleCards.length === 0 ? (
              <div className="triumph-empty-results">{t('learningNavigation.triumph.noMatches')}</div>
            ) : (
              <div className="triumph-card-list">
                {visibleCards.map((card) => {
                  const isReadingCard = PracticeSettingsService.getCardType(card) ===
                    PracticeSettingsService.CARD_TYPES.READING;

                  return (
                    <article
                      className={`triumph-card-row ${isReadingCard ? 'triumph-card-row-reading' : ''} ${matchesTriumphHighlight(card, highlightedCardId) ? 'is-home-highlight' : ''}`.trim()}
                      key={card.id}
                    >
                      <img
                        src={getGraduationStamp(
                          PracticeSettingsService.getCardGraduationType(card) ||
                          PracticeSettingsService.GRADUATION_TYPES.MANUAL
                        )}
                        alt=""
                        className="triumph-card-stamp"
                      />
                      <div className="triumph-card-copy">
                        {isReadingCard ? (
                          <>
                            <div className="triumph-card-front triumph-card-reading-title">
                              <MarkdownText text={card.readingTitle || card.front || ''} />
                            </div>
                            <div className="triumph-card-back triumph-card-reading-preview">
                              <MarkdownText text={card.readingContent || card.back || ''} />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="triumph-card-front">
                              <MarkdownText text={card.front} />
                            </div>
                            <div className="triumph-card-back">
                              <MarkdownText text={card.back} />
                            </div>
                          </>
                        )}
                        <div className="triumph-card-meta">
                          <span>{t(`learningNavigation.common.${isReadingCard ? 'reading' : PracticeSettingsService.getCardType(card) === 'simple' ? 'short' : 'sentence'}`)}</span>
                          <span>{t('learningNavigation.triumph.reps', undefined, { count: formatNumber(PracticeSettingsService.getCardPracticeCount(card)) })}</span>
                          <span>{t('learningNavigation.triumph.score', undefined, { count: formatNumber(PracticeSettingsService.getCardScore(card)) })}</span>
                          <span>{formatDate(getCompletedAt(card))}</span>
                        </div>
                      </div>
                      <div className="triumph-card-actions">
                        {(card.isReadingGroup
                          ? (card.readingChildren || []).some(isTriumphLevelUpEligible)
                          : isTriumphLevelUpEligible(card)) && (
                          <button
                            type="button"
                            className="triumph-level-up-button"
                            onClick={() => openLevelUp(card)}
                            aria-label={t('learningNavigation.triumph.createLevelUp')}
                            title={t('learningNavigation.triumph.createLevelUp')}
                          >
                            <RotateCcw size={20} aria-hidden="true" />
                          </button>
                        )}
                        <button
                          type="button"
                          className={`triumph-favorite-button ${card.isFavorite ? 'active' : ''}`.trim()}
                          onClick={() => handleFavoriteToggle(card)}
                          disabled={savingFavoriteId === card.id}
                          aria-label={t(card.isFavorite ? 'learningNavigation.triumph.removeFavorite' : 'learningNavigation.triumph.addFavorite')}
                          aria-pressed={Boolean(card.isFavorite)}
                        >
                          <Heart size={20} fill={card.isFavorite ? 'currentColor' : 'none'} aria-hidden="true" />
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}

            <div className="triumph-pagination">
              <button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={safePage <= 1}>
                <ChevronLeft size={17} aria-hidden="true" />
                <span>{t('learningNavigation.common.previous')}</span>
              </button>
              <span>{safePage} / {totalPages}</span>
              <button type="button" onClick={() => setPage((value) => Math.min(totalPages, value + 1))} disabled={safePage >= totalPages}>
                <span>{t('learningNavigation.common.next')}</span>
                <ChevronRight size={17} aria-hidden="true" />
              </button>
            </div>
          </section>
        </>
      )}
      {levelUpCard && <ReviewLevelUpDialog card={levelUpCard} onClose={() => setLevelUpCard(null)} />}
      {readingPicker && (
        <div className="triumph-level-up-overlay" onClick={() => setReadingPicker(null)}>
          <div className="triumph-level-up-modal" onClick={(event) => event.stopPropagation()}>
            <div className="triumph-level-up-header">
              <div>
                <h2>{readingPicker.group.readingTitle || t('learningNavigation.levelUp.readingSentences')}</h2>
                <p>{t('learningNavigation.levelUp.chooseSentence')}</p>
              </div>
              <button type="button" onClick={() => setReadingPicker(null)} aria-label={t('learningNavigation.levelUp.closePicker')}>{'×'}</button>
            </div>
            <div className="triumph-reading-picker">
              {readingPicker.children.map((child) => (
                <button
                  type="button"
                  key={child.id}
                  onClick={() => {
                    setReadingPicker(null);
                    setLevelUpCard(child);
                  }}
                >
                  <span>{t(child.readingItemType === 'speech' ? 'learningNavigation.common.speech' : 'learningNavigation.common.story')}</span>
                  <MarkdownText text={child.suggestedAnswer || child.back} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <CelebrationPopup
        isVisible={Boolean(levelUpCelebration)}
        onClose={() => setLevelUpCelebration(null)}
        title={t('levelUp.title')}
        message={levelUpCelebration ? t('levelUp.successMessage', undefined, { level: levelUpCelebration.level }) : ''}
        {...getCardLevelUpAnimation(levelUpCelebration?.level)}
      />
    </div>
  );
}

export default TriumphPage;
